<template>
  <div
    class="tutorial absolute w-full h-full left-0 bottom-0 z-101 fadein"
    role="dialog"
    aria-modal="true"
    aria-labelledby="tutorial-heading"
    @click="checkAndClose($event)"
    @touchstart="checkAndClose($event)"
    @keydown.esc="close()"
  >
    <div class="absolute right-0 bottom-0 h-full md:w-1/2 xl:w-1/3 w-full bg-slate-middle flex justify-center items-center">
      <div class="py-7 px-7 flex justify-between flex-col h-full fadein">
        <div class="p-1 tutorial-wrapper">
          <h2 id="tutorial-heading" ref="heading" tabindex="-1" class="font-bold text-4xl text-white focus:outline-none">{{ currentStep.title }}</h2>
          <div class="h-px bg-slate-bright mx-3 mt-3"></div>
          <div class="step-wrapper mt-7 flex flex-wrap rounded justify-center overflow-scroll">
            <div class="flex flex-row">
              <div class="border-blue-left mx-3 rounded-xl"></div>
              <p class="text-white text-left bord text-lg">{{ currentStep.description }}</p>
            </div>
            <figure v-if="currentStep.image" class="px-4 w-full flex flex-col justify-center text-left xl:text-lg text-alt-white">
              <img class="w-full slow-fadein rounded-xl" :src="currentStep.image.src" :alt="currentStep.image.description" />
              <figcaption v-if="currentStep.image.description" class="italic mt-3">{{ currentStep.image.description }}</figcaption>
            </figure>
            <div v-if="currentStep.elaboration" class="flex flex-row mt-4 mb-11">
              <div class="border-orange-left mx-3 rounded-xl"></div>
              <p class="text-white text-left bord text-lg">{{ currentStep.elaboration }}</p>
            </div>
          </div>
        </div>
        <div>
          <div class="mt-5 flex justify-center items-center">
            <button v-if="state.currentIndex === 0" type="button" class="bg-slate-dark mx-2 p-2 rounded-full" @click="close()">
              <img class="w-5 h-5 invert rotate-45" :src="Close" :alt="labels.close" />
            </button>
            <button v-if="state.currentIndex > 0" type="button" class="bg-slate-dark mx-2 p-2 rounded-full" @click="navigateTutorialStep(-1)">
              <img class="w-5 h-5 invert" :src="Arrow" :alt="labels.previous" />
            </button>
            <p class="sr-only" aria-live="polite">{{ labels.step(state.currentIndex + 1, steps.length) }}</p>
            <div class="flex flex-row justify-center" aria-hidden="true">
              <div v-for="(step, index) in steps.length" :key="index">
                <div class="w-3 h-3 rounded-full mx-1" :class="stepsStyles(index)"></div>
              </div>
            </div>
            <button
              v-if="state.currentIndex + 1 < steps.length"
              type="button"
              class="bg-blue-middle mx-2 p-2 rounded-full"
              @click="navigateTutorialStep(1)"
            >
              <img class="w-5 h-5 invert rotate-180" :src="Arrow" :alt="labels.next" />
            </button>
            <button v-if="state.currentIndex + 1 === steps.length" type="button" class="bg-blue-middle mx-2 p-2 rounded-full" @click="close()">
              <img class="w-5 h-5 invert" :src="Accept" :alt="labels.finish" />
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, onMounted, reactive, ref } from 'vue'

  import { TutorialStep } from '@/common/interfaces'
  import useCommonStore from '@/composables/common'

  import Arrow from '@/assets/icons/fontawesome/angle-left-solid.svg'
  import Close from '@/assets/icons/fontawesome/plus-solid.svg'
  import Accept from '@/assets/icons/fontawesome/circle-check-solid.svg'

  import Smiley from '@/assets/tutorial/smiley.png'

  const common = useCommonStore()

  const tutorialSteps: Record<'no' | 'en', TutorialStep[]> = {
    no: [
      {
        title: 'Velkommen til Talkwall-guiden!',
        description: 'Denne guiden viser deg de viktigste funksjonene på en tavle.',
        image: { src: Smiley, description: '' },
      },
      {
        title: 'Legg til et bidrag',
        description:
          'Trykk på plussknappen for å åpne «Nytt bidrag». Skriv teksten din, legg eventuelt ved et bilde eller en lenke, og trykk «Legg til».',
        elaboration:
          'Er sidepanelet åpent, havner bidraget der, og du kan dra det ut på tavla. Er sidepanelet lukket, plasseres bidraget midt på tavla.',
      },
      {
        title: 'Flytt rundt på tavla',
        description:
          'Med mus: klikk på et tomt område og dra. Med styreflate: sveip med to fingre. På nettbrett og mobil: dra med én finger på et tomt område.',
        elaboration: 'Med tastatur: gå til tavla med Tab og bruk piltastene. Pluss og minus zoomer, og 0 tilbakestiller zoomen.',
      },
      {
        title: 'Zoom inn og ut',
        description:
          'Med mus: bruk scrollhjulet. Med styreflate eller berøringsskjerm: knip med to fingre. Du kan også bruke pluss- og minusknappene ved siden av tavla.',
        elaboration: 'Zoomen følger musepekeren eller fingrene dine, så du kommer rett til den delen av tavla du vil se nærmere på.',
      },
      {
        title: 'Flytt et bidrag',
        description:
          'Trykk og hold på et bidrag, og dra det dit du vil ha det. Et klikk velger bidraget. Legg til flere med Shift-klikk eller «Velg» i bidragsmenyen, så flytter du alle når du drar ett av dem. På nettbrett og mobil trykker du på flere bidrag for å velge dem. Klikk på et tomt område for å fjerne valget.',
        elaboration:
          'Med tastatur: gå til et bidrag med Tab, trykk mellomrom for å velge det, og flytt valgte bidrag med piltastene. Escape fjerner valget. Etter at du har klikket på et bidrag, flytter piltastene det også.',
      },
    ],
    en: [
      {
        title: 'Welcome to the Talkwall tutorial!',
        description: 'This tutorial walks you through the most important features of a wall.',
        image: { src: Smiley, description: '' },
      },
      {
        title: 'Add a contribution',
        description: 'Press the plus button to open "New contribution". Write your text, optionally attach an image or a link, and press "Add".',
        elaboration:
          'If the sidebar is open, the contribution appears there and you can drag it onto the board. If the sidebar is closed, it is placed in the middle of the board.',
      },
      {
        title: 'Move around the board',
        description:
          'With a mouse: click an empty area and drag. With a trackpad: swipe with two fingers. On tablets and phones: drag an empty area with one finger.',
        elaboration: 'With a keyboard: Tab to the board and use the arrow keys. Plus and minus zoom, and 0 resets the zoom.',
      },
      {
        title: 'Zoom in and out',
        description:
          'With a mouse: use the scroll wheel. With a trackpad or touch screen: pinch with two fingers. You can also use the plus and minus buttons beside the board.',
        elaboration: 'Zooming follows your pointer or fingers, so you land right on the part of the board you want a closer look at.',
      },
      {
        title: 'Move a contribution',
        description:
          'Press and hold a contribution, then drag it where you want it. A click selects it. Add more with Shift-click or "Select" in the contribution menu, and dragging one moves them all. On tablets and phones, tap more contributions to select them. Click an empty area to clear the selection.',
        elaboration:
          'With a keyboard: Tab to a contribution, press Space to select it, and move the selected contributions with the arrow keys. Escape clears the selection. After clicking a contribution, the arrow keys move it too.',
      },
    ],
  }

  const state = reactive({
    currentIndex: 0,
  })

  const emit = defineEmits<{
    (e: 'close'): void
  }>()

  const buttonLabels = {
    no: {
      close: 'Lukk guiden',
      previous: 'Forrige steg',
      next: 'Neste steg',
      finish: 'Fullfør guiden',
      step: (n: number, total: number) => `Steg ${n} av ${total}`,
    },
    en: {
      close: 'Close the guide',
      previous: 'Previous step',
      next: 'Next step',
      finish: 'Finish the guide',
      step: (n: number, total: number) => `Step ${n} of ${total}`,
    },
  }

  const heading = ref<HTMLHeadingElement>()
  onMounted(() => heading.value?.focus())

  const language = computed(() => (common.getters.languageCode.value.includes('no') ? 'no' : 'en'))
  const labels = computed(() => buttonLabels[language.value])
  const steps = computed(() => tutorialSteps[language.value])
  const currentStep = computed(() => steps.value[state.currentIndex])

  function navigateTutorialStep(change: number) {
    state.currentIndex += change
  }

  function stepsStyles(index: number) {
    return index < state.currentIndex ? 'bg-blue-middle' : 'bg-slate-dark'
  }

  function checkAndClose(event: MouseEvent | TouchEvent) {
    // Keyboard-activated buttons fire a click at pageX 0, which would otherwise read as a click outside the panel
    if ((event.target as Element).closest('button')) return
    const threshold = (window.innerWidth / 100) * ((2 / 3) * 100)
    const x = event instanceof MouseEvent ? event.pageX : event.touches[0].pageX
    if (x <= threshold) close()
  }

  function close() {
    emit('close')
  }
</script>

<style scoped>
  .tutorial {
    background-color: rgba(0, 0, 0, 0.7);
  }
  .step-wrapper {
    max-height: 75vh;
  }
  .tutorial-wrapper {
    height: 90%;
    overflow-y: hidden;
  }
</style>
